/**
 * @fileoverview Utility functions for Hydraulic Balancing Simulation
 * 
 * Contains scientifically accurate calculations for:
 * - Temperature conversions and utilities
 * - Heat transfer (radiators, transmission, ventilation)
 * - Hydraulic calculations (pressure loss, flow rates, valve sizing)
 * - Heat pump performance (COP calculation)
 * - Hydraulic balancing algorithms
 * - Building structure validation
 * 
 * Architecture follows similar principles to HeatPumpSimulation/utils.js:
 * - Modular, reusable calculation functions
 * - Consistent parameter naming and units
 * - Detailed JSDoc documentation with formulas
 * - Clear separation of concerns by region
 * 
 * @module HydraulicBalancing/utils
 * @author HeatCraftHP Team
 * @version 2.1.0
 */

import {
  WATER_PROPERTIES,
  AIR_PROPERTIES,
  GRAVITY,
  RADIATOR_CONFIG,
  VALVE_CONFIG,
  PIPING_CONFIG,
  HEAT_PUMP_CONFIG,
  ROOM_DEFAULTS,
  BUILDING_CONFIG,
  SIMULATION_CONFIG,
} from './constants';

// #region GENERAL UTILITIES
// ============================================================================
// GENERAL UTILITIES
// ============================================================================

/**
 * Clamps a value between a minimum and maximum
 * @param {number} value - Value to clamp
 * @param {number} min - Minimum allowed value
 * @param {number} max - Maximum allowed value
 * @returns {number} Clamped value
 */
export const clamp = (value, min, max) => {
  return Math.max(min, Math.min(max, value));
};

/**
 * Formats a number for display with specified decimal places
 * @param {number} value - Number to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted string
 */
export const formatNumber = (value, decimals = 2) => {
  if (typeof value !== 'number' || isNaN(value)) return '—';
  return value.toFixed(decimals);
};

// #endregion GENERAL UTILITIES

// #region TEMPERATURE UTILITIES
// ============================================================================
// TEMPERATURE UTILITIES
// ============================================================================

/**
 * Converts Celsius to Kelvin
 * @param {number} celsius - Temperature in Celsius
 * @returns {number} Temperature in Kelvin
 */
export const celsiusToKelvin = (celsius) => celsius + 273.15;

/**
 * Converts Kelvin to Celsius
 * @param {number} kelvin - Temperature in Kelvin
 * @returns {number} Temperature in Celsius
 */
export const kelvinToCelsius = (kelvin) => kelvin - 273.15;

/**
 * Calculates the logarithmic mean temperature difference (LMTD)
 * Used for accurate heat exchanger calculations
 * 
 * LMTD = (ΔT₁ - ΔT₂) / ln(ΔT₁ / ΔT₂)
 * 
 * @param {number} Tin - Inlet temperature [°C]
 * @param {number} Tout - Outlet temperature [°C]
 * @param {number} Troom - Room temperature [°C]
 * @returns {number} LMTD [K]
 */
export const calculateLMTD = (Tin, Tout, Troom) => {
  const dt1 = Tin - Troom;
  const dt2 = Tout - Troom;
  
  // Avoid division by zero when temperatures are equal
  if (Math.abs(dt1 - dt2) < 0.001) {
    return dt1;
  }
  
  // Avoid log of negative or zero
  if (dt1 <= 0 || dt2 <= 0) {
    return Math.max(dt1, dt2, 0);
  }
  
  return (dt1 - dt2) / Math.log(dt1 / dt2);
};

/**
 * Calculates arithmetic mean temperature difference
 * Simplified alternative to LMTD for smaller temperature differences
 * 
 * ΔT_mean = (T_in + T_out) / 2 - T_room
 * 
 * @param {number} Tin - Inlet temperature [°C]
 * @param {number} Tout - Outlet temperature [°C]
 * @param {number} Troom - Room temperature [°C]
 * @returns {number} Mean temperature difference [K]
 */
export const calculateMeanDT = (Tin, Tout, Troom) => {
  return ((Tin + Tout) / 2) - Troom;
};

// #endregion TEMPERATURE UTILITIES

// #region ROOM GEOMETRY
// ============================================================================
// ROOM GEOMETRY CALCULATIONS
// ============================================================================

/**
 * Calculates room volume from building envelope areas
 * Similar to calculateRoomVolume in HeatPumpSimulation/utils.js
 * 
 * Estimates volume using floor area (roof) and wall area to determine ceiling height
 * 
 * @param {number} floorArea - Floor/roof area [m²]
 * @param {number} wallArea - Total wall surface area [m²] (optional)
 * @returns {number} Estimated room volume [m³]
 */
export const calculateRoomVolume = (floorArea, wallArea = 0) => {
  // If no wall area provided, use building config dimensions
  if (wallArea <= 0) {
    return floorArea * BUILDING_CONFIG.ROOM_HEIGHT;
  }
  
  // Estimate ceiling height from wall area
  // Assuming roughly square room: perimeter ≈ 4 × √(floorArea)
  // wallArea ≈ perimeter × height
  // Therefore: height ≈ wallArea / (4 × √(floorArea))
  const estimatedPerimeter = 4 * Math.sqrt(Math.max(floorArea, 1));
  const estimatedHeight = wallArea / estimatedPerimeter;
  
  // Clamp height to reasonable range (2.0m to 4.0m)
  const ceilingHeight = clamp(estimatedHeight, 2.0, 4.0);
  
  return floorArea * ceilingHeight;
};

/**
 * Gets the default room volume from building configuration
 * @returns {number} Default room volume [m³]
 */
export const getDefaultRoomVolume = () => {
  return BUILDING_CONFIG.ROOM_WIDTH * BUILDING_CONFIG.ROOM_HEIGHT * BUILDING_CONFIG.ROOM_DEPTH;
};

// #endregion ROOM GEOMETRY

// #region RADIATOR HEAT TRANSFER CALCULATIONS
// ============================================================================
// RADIATOR HEAT TRANSFER CALCULATIONS
// ============================================================================

/**
 * Calculates radiator heat output using EN 442 standard formula
 * 
 * Q = K × n_sections × sign(ΔT_m) × |ΔT_m|^n
 * 
 * The radiator exponent n ≈ 1.3 accounts for combined convection and radiation.
 * Handles both heating (positive ΔT) and cooling (negative ΔT) scenarios.
 * 
 * Positive values = heat output from radiator (typical heating).
 * Negative values = heat absorption by radiator (cooling).
 * 
 * @param {Object} params - Calculation parameters
 * @param {number} params.nSections - Number of radiator sections
 * @param {number} params.kRad - Heat emission coefficient per section [W/K^n]
 * @param {number} params.meanDT - Mean temperature difference [K]
 * @returns {number} Heat output [W] (negative = cooling/absorption)
 */
export const calculateRadiatorOutput = ({ nSections, kRad, meanDT }) => {
  const n = RADIATOR_CONFIG.RADIATOR_EXPONENT;
  
  // Handle sign separately: Q = sign(ΔT) × K × |ΔT|^n
  // This preserves direction of heat flow while maintaining non-linear relationship
  const sign = Math.sign(meanDT);
  const magnitude = Math.pow(Math.abs(meanDT), n);
  
  return sign * nSections * kRad * magnitude;
};

/**
 * Calculates nominal radiator power at standard test conditions (75/65/20°C)
 * 
 * @param {number} nSections - Number of radiator sections
 * @param {number} kRad - Heat emission coefficient per section [W/K^n]
 * @returns {number} Nominal power [W]
 */
export const calculateNominalRadiatorPower = (nSections, kRad) => {
  const { TEST_SUPPLY_TEMP, TEST_RETURN_TEMP, TEST_ROOM_TEMP, RADIATOR_EXPONENT } = RADIATOR_CONFIG;
  const nominalDT = ((TEST_SUPPLY_TEMP + TEST_RETURN_TEMP) / 2) - TEST_ROOM_TEMP;
  
  return nSections * kRad * Math.pow(nominalDT, RADIATOR_EXPONENT);
};

/**
 * Calculates radiator heat output using iterative method
 * Similar to calculateRadiatorHeatTransfer in HeatPumpSimulation/utils.js
 * 
 * Uses energy balance: Q_water = Q_radiator
 * ṁ × cp × (Tin - Tout) = K × (ΔT_m)^n
 * 
 * @param {Object} params - Calculation parameters
 * @param {number} params.supplyTemperature - Supply water temperature [°C]
 * @param {number} params.roomTemperature - Room temperature [°C]
 * @param {number} params.volumetricFlow - Volume flow rate [m³/s]
 * @param {number} params.valvePosition - Preset valve position (0-1)
 * @param {number} params.thermostatOpening - Thermostat valve opening (0-1)
 * @param {number} params.nSections - Number of radiator sections
 * @param {number} params.kRad - Heat emission coefficient [W/K^n]
 * @returns {Object} { heatOutput, returnTemperature, massFlow }
 */
export const calculateRadiatorHeatTransfer = ({
  supplyTemperature,
  roomTemperature,
  volumetricFlow,
  valvePosition,
  thermostatOpening,
  nSections,
  kRad,
}) => {
  const { SPECIFIC_HEAT } = WATER_PROPERTIES;
  const iterations = SIMULATION_CONFIG.RADIATOR_ITERATIONS || 3;
  
  // Calculate effective mass flow
  const effectiveFlow = volumetricFlow * valvePosition * thermostatOpening;
  const massFlow = WATER_PROPERTIES.DENSITY * effectiveFlow;
  
  // Initial guess for return temperature
  let returnTemperature = supplyTemperature - 5;
  
  // Iterative calculation for return temperature (thermal equilibrium)
  for (let i = 0; i < iterations; i++) {
    const meanTemperature = (supplyTemperature + returnTemperature) / 2;
    const tempDifference = meanTemperature - roomTemperature;
    const heatOutput = calculateRadiatorOutput({ nSections, kRad, meanDT: tempDifference });
    
    // Calculate new return temperature from energy balance
    if (massFlow > 1e-6) {
      returnTemperature = supplyTemperature - heatOutput / (massFlow * SPECIFIC_HEAT);
    } else {
      returnTemperature = supplyTemperature;
    }
  }
  
  // Final heat output calculation (no clamping - allow cooling mode)
  const meanTemperature = (supplyTemperature + returnTemperature) / 2;
  const tempDifference = meanTemperature - roomTemperature;
  
  const heatByWater = massFlow * SPECIFIC_HEAT * (supplyTemperature - returnTemperature);
  const heatByRadiator = calculateRadiatorOutput({ nSections, kRad, meanDT: tempDifference });
  const actualHeat = Math.min(heatByWater, heatByRadiator);
  
  return {
    heatOutput: Math.max(actualHeat, 0),
    returnTemperature,
    massFlow,
  };
};

/**
 * Iteratively calculates radiator return temperature and performance
 * 
 * Uses energy balance: Q_water = Q_radiator
 * ṁ × cp × (Tin - Tout) = K × (ΔT_m)^n
 * 
 * @param {Object} params - Calculation parameters
 * @param {number} params.Tsupply - Supply temperature [°C]
 * @param {number} params.Troom - Room temperature [°C]
 * @param {number} params.massFlow - Mass flow rate [kg/s]
 * @param {number} params.nSections - Number of radiator sections
 * @param {number} params.kRad - Heat emission coefficient [W/K^n]
 * @returns {Object} { Treturn, heatOutput, meanDT }
 */
export const calculateRadiatorPerformance = ({
  Tsupply,
  Troom,
  massFlow,
  nSections,
  kRad,
}) => {
  const { SPECIFIC_HEAT } = WATER_PROPERTIES;
  const iterations = SIMULATION_CONFIG.RADIATOR_ITERATIONS || 3;
  
  // Minimum flow threshold to prevent numerical instability
  // 1e-4 kg/s = 0.1 g/s = ~6 ml/min - below this, consider "no flow"
  const MIN_FLOW_THRESHOLD = 1e-4;
  
  // If flow is too low, no meaningful heat transfer occurs
  if (massFlow < MIN_FLOW_THRESHOLD) {
    return { Treturn: Tsupply, heatOutput: 0, meanDT: 0 };
  }
  
  // Initial guess for return temperature
  let Treturn = Tsupply - 10;
  let heatOutput = 0;
  let meanDT = 0;
  
  // Iterative solution for thermal equilibrium
  for (let i = 0; i < iterations; i++) {
    meanDT = calculateMeanDT(Tsupply, Treturn, Troom);
    heatOutput = calculateRadiatorOutput({ nSections, kRad, meanDT });
    
    // Calculate return temperature from energy balance
    const deltaT = heatOutput / (massFlow * SPECIFIC_HEAT);
    
    // Clamp deltaT to physically reasonable range (max 50°C drop across radiator)
    const clampedDeltaT = Math.max(-50, Math.min(50, deltaT));
    
    // Return temperature cannot be lower than room temperature (physical limit)
    Treturn = Math.max(Troom, Tsupply - clampedDeltaT);
  }
  
  return { Treturn, heatOutput, meanDT };
};

// #endregion RADIATOR HEAT TRANSFER CALCULATIONS

// #region HYDRAULIC CALCULATIONS
// ============================================================================
// HYDRAULIC CALCULATIONS
// ============================================================================

/**
 * Calculates volumetric flow rate through pipes using simplified Darcy-Weisbach
 * Similar to calculateVolumetricFlow in HeatPumpSimulation/utils.js
 * 
 * Uses simplified flow calculation for quick estimates
 * 
 * @param {number} systemPressure - System pressure [bar]
 * @param {number} pipeLength - Effective pipe length [m]
 * @param {number} pipeDiameter - Pipe diameter [mm]
 * @returns {number} Volumetric flow rate [m³/s]
 */
export const calculateVolumetricFlow = (systemPressure, pipeLength, pipeDiameter) => {
  const pressurePascals = systemPressure * 100000; // bar to Pa
  const diameterMeters = pipeDiameter * 0.001; // mm to m
  const pipeArea = Math.PI * Math.pow(diameterMeters, 2) / 4;
  
  // Simplified Darcy-Weisbach: v = √(2 × D × ΔP / (f × L × ρ))
  const frictionCoefficient = PIPING_CONFIG.FRICTION_FACTOR || 0.03;
  
  const flowVelocity = Math.sqrt(
    (2 * diameterMeters * pressurePascals) /
    (frictionCoefficient * Math.max(pipeLength, 0.1) * WATER_PROPERTIES.DENSITY)
  );
  
  return pipeArea * flowVelocity;
};

/**
 * Calculates Reynolds number for pipe flow
 * 
 * Re = (ρ × v × D) / μ = (4 × ṁ) / (π × D × μ)
 * 
 * @param {number} massFlow - Mass flow rate [kg/s]
 * @param {number} diameter - Pipe inner diameter [m]
 * @returns {number} Reynolds number (dimensionless)
 */
export const calculateReynoldsNumber = (massFlow, diameter) => {
  const { DENSITY, VISCOSITY } = WATER_PROPERTIES;
  
  if (massFlow < 1e-9 || diameter <= 0) return 0;
  
  // Velocity = ṁ / (ρ × A)
  const area = Math.PI * Math.pow(diameter, 2) / 4;
  const velocity = massFlow / (DENSITY * area);
  
  return (DENSITY * velocity * diameter) / VISCOSITY;
};

/**
 * Calculates Darcy friction factor using Swamee-Jain equation
 * Valid for turbulent flow in rough pipes (Re > 4000)
 * 
 * @param {number} reynoldsNumber - Reynolds number
 * @param {number} diameter - Pipe diameter [m]
 * @param {number} roughness - Pipe roughness [m]
 * @returns {number} Friction factor (dimensionless)
 */
export const calculateFrictionFactor = (reynoldsNumber, diameter, roughness = PIPING_CONFIG.PIPE_ROUGHNESS / 1000) => {
  // Laminar flow
  if (reynoldsNumber < 2300) {
    return 64 / reynoldsNumber;
  }
  
  // Transitional - use average
  if (reynoldsNumber < 4000) {
    const laminar = 64 / reynoldsNumber;
    const turbulent = 0.25 / Math.pow(
      Math.log10((roughness / (3.7 * diameter)) + (5.74 / Math.pow(reynoldsNumber, 0.9))),
      2
    );
    return (laminar + turbulent) / 2;
  }
  
  // Turbulent flow - Swamee-Jain equation
  return 0.25 / Math.pow(
    Math.log10((roughness / (3.7 * diameter)) + (5.74 / Math.pow(reynoldsNumber, 0.9))),
    2
  );
};

/**
 * Calculates pressure loss in a pipe using Darcy-Weisbach equation
 * 
 * Δp = f × (L/D) × (ρv²/2)
 * 
 * @param {Object} params - Calculation parameters
 * @param {number} params.massFlow - Mass flow rate [kg/s]
 * @param {number} params.length - Pipe length [m]
 * @param {number} params.diameter - Pipe inner diameter [m]
 * @returns {number} Pressure loss [Pa]
 */
export const calculatePipePressureLoss = ({ massFlow, length, diameter }) => {
  const { DENSITY } = WATER_PROPERTIES;
  
  if (massFlow < 1e-9) return 0;
  
  const area = Math.PI * Math.pow(diameter, 2) / 4;
  const velocity = massFlow / (DENSITY * area);
  const Re = calculateReynoldsNumber(massFlow, diameter);
  const f = calculateFrictionFactor(Re, diameter);
  
  // Include equivalent length for fittings
  const effectiveLength = length * PIPING_CONFIG.FITTING_FACTOR;
  
  return f * (effectiveLength / diameter) * (DENSITY * Math.pow(velocity, 2) / 2);
};

/**
 * Calculates hydrostatic pressure difference due to height
 * 
 * Δp = ρ × g × h
 * 
 * @param {number} height - Height difference [m]
 * @returns {number} Pressure difference [Pa]
 */
export const calculateHydrostaticPressure = (height) => {
  return WATER_PROPERTIES.DENSITY * GRAVITY * height;
};

/**
 * Calculates available pressure at a given floor
 * Higher floors have more pressure available due to pump head
 * 
 * @param {number} floorIndex - Floor number (0 = ground floor)
 * @param {number} systemPressure - System differential pressure [Pa]
 * @param {number} totalFloors - Total number of floors
 * @returns {number} Available pressure [Pa]
 */
export const calculateAvailablePressure = (floorIndex, systemPressure, totalFloors) => {
  // Pressure increases with height due to pump characteristic
  // Higher floors have more available ΔP but longer pipe runs
  const heightFactor = 1 + (floorIndex * PIPING_CONFIG.FLOOR_HEIGHT * GRAVITY * WATER_PROPERTIES.DENSITY / systemPressure * 0.1);
  
  return systemPressure * heightFactor;
};

/**
 * Calculates flow rate through a valve using the Kv equation
 * 
 * V̇ = Kv × √(Δp / ρ_ref)
 * 
 * where ρ_ref = 1000 kg/m³ (reference density for Kv definition)
 * 
 * @param {number} kv - Flow coefficient [m³/h at 1 bar]
 * @param {number} pressureDrop - Pressure drop across valve [Pa]
 * @returns {number} Volume flow rate [m³/s]
 */
export const calculateValveFlow = (kv, pressureDrop) => {
  if (pressureDrop <= 0) return 0;
  
  // Convert Kv from m³/h to m³/s and pressure from Pa to bar
  const kvSI = kv / 3600; // m³/s per √bar
  const dpBar = pressureDrop / 100000; // Pa to bar
  
  return kvSI * Math.sqrt(dpBar);
};

/**
 * Calculates effective Kv value for a valve with preset and thermostat
 * 
 * Kv_eff = Kv_max × preset × thermostat_position
 * 
 * @param {number} kvMax - Maximum Kv value [m³/h]
 * @param {number} presetOpening - Preset valve opening (0-1)
 * @param {number} thermostatOpening - Thermostat valve opening (0-1)
 * @returns {number} Effective Kv [m³/h]
 */
export const calculateEffectiveKv = (kvMax, presetOpening, thermostatOpening) => {
  return kvMax * Math.max(presetOpening, VALVE_CONFIG.MIN_OPENING) * thermostatOpening;
};

// #endregion HYDRAULIC CALCULATIONS

// #region HYDRAULIC BALANCING CALCULATIONS
// ============================================================================
// HYDRAULIC BALANCING CALCULATIONS
// ============================================================================

/**
 * Calculates recommended preset valve position based on hydraulic balancing principles
 * 
 * Physics-based approach:
 * - Ground floor: HIGH available pressure (no height loss) → needs MORE restriction (lower preset)
 * - Top floor: LOW available pressure (max height loss) → needs LESS restriction (higher preset)
 * - Distant rooms: Additional pipe friction → needs less restriction
 * 
 * ΔP_available = ΔP_system - ρgh - ΔP_pipe_friction
 * Preset compensates for pressure differences to achieve balanced flow
 * 
 * @param {number} floorIndex - Floor number (0 = ground floor)
 * @param {number} columnIndex - Position on floor (0 = closest to riser)
 * @param {number} totalFloors - Total number of floors in building
 * @param {number} systemPressure - System differential pressure [bar]
 * @param {number} pipeDiameter - Pipe diameter [mm] (optional, defaults to 15mm)
 * @returns {number} Recommended preset opening (0-1)
 */
export const calculateRecommendedPreset = (floorIndex, columnIndex, totalFloors, systemPressure, pipeDiameter = PIPING_CONFIG.DEFAULT_PIPE_DIAMETER) => {
  // Convert system pressure to Pa
  const systemPressurePa = systemPressure * 100000;
  
  // Calculate hydrostatic pressure loss (ΔP = ρ × g × h)
  const height = floorIndex * PIPING_CONFIG.FLOOR_HEIGHT; // meters
  const hydrostaticLoss = WATER_PROPERTIES.DENSITY * GRAVITY * height; // Pa
  
  // Calculate pipe friction losses (longer pipes to distant rooms and higher floors)
  const equivalentPipeLength = (floorIndex * PIPING_CONFIG.FLOOR_HEIGHT * 2) + (columnIndex * 3); // meters
  const estimatedMassFlow = PIPING_CONFIG.DESIGN_FLOW_RATE; // kg/s (configurable design flow)
  const pipeLoss = calculatePipePressureLoss({
    massFlow: estimatedMassFlow,
    length: equivalentPipeLength,
    diameter: pipeDiameter / 1000, // mm to m
  });
  
  // Available pressure at this location
  const availablePressure = systemPressurePa - hydrostaticLoss - pipeLoss;
  
  // Reference pressure (ground floor, closest position)
  const referencePressure = systemPressurePa;
  
  // Preset ratio: locations with LESS available pressure need LARGER openings
  // Ground floor (high pressure) → small preset (0.3-0.5)
  // Top floor (low pressure) → large preset (0.7-1.0)
  const pressureRatio = availablePressure / referencePressure;
  
  // Inverse relationship: less pressure = more opening needed
  const basePreset = 1.0 / Math.sqrt(pressureRatio);
  
  // Apply safety limits and practical constraints
  const preset = Math.max(0.2, Math.min(1.0, basePreset));
  
  return preset;
};

/**
 * Calculates volume flow rate through a pipe branch
 * considering pipe resistance and valve settings
 * 
 * @param {Object} params - Calculation parameters
 * @param {number} params.floorIndex - Floor level
 * @param {number} params.columnIndex - Position on floor
 * @param {number} params.systemPressure - System pressure [bar]
 * @param {number} params.pipeLength - Branch pipe length [m]
 * @param {number} params.pipeDiameter - Pipe diameter [mm]
 * @param {number} params.kvMax - Maximum valve Kv
 * @param {number} params.presetOpening - Preset valve position
 * @param {number} params.thermostatOpening - Thermostat position
 * @returns {number} Volume flow rate [m³/s]
 */
export const calculateBranchFlow = ({
  floorIndex,
  columnIndex,
  systemPressure,
  pipeLength,
  pipeDiameter,
  kvMax,
  presetOpening,
  thermostatOpening,
}) => {
  // Convert units
  const pressurePa = systemPressure * 100000; // bar to Pa
  const diameterM = pipeDiameter / 1000; // mm to m
  
  // Calculate effective pipe length (includes vertical run)
  const effectiveLength = pipeLength * (columnIndex + 1) + (floorIndex * PIPING_CONFIG.FLOOR_HEIGHT);
  
  // Calculate effective valve Kv
  const kvEff = calculateEffectiveKv(kvMax, presetOpening, thermostatOpening);
  
  // Iterative solution for flow (pressure = pipe loss + valve loss)
  let flow = 0.0001; // Initial guess [m³/s]
  
  for (let i = 0; i < 10; i++) {
    const massFlow = flow * WATER_PROPERTIES.DENSITY;
    const pipeLoss = calculatePipePressureLoss({
      massFlow,
      length: effectiveLength * 2, // Supply + return
      diameter: diameterM,
    });
    
    const availablePressure = pressurePa - pipeLoss;
    
    if (availablePressure > 0) {
      flow = calculateValveFlow(kvEff, availablePressure);
    } else {
      flow = 0;
      break;
    }
  }
  
  return flow;
};

// #endregion HYDRAULIC BALANCING CALCULATIONS

// #region HEAT LOSS CALCULATIONS
// ============================================================================
// HEAT LOSS CALCULATIONS
// ============================================================================

/**
 * Calculates transmission heat loss through building envelope
 * 
 * Q = U × A × (T_inside - T_outside)
 * 
 * Positive values represent heat loss (room warmer than outside).
 * Negative values represent heat gain (outside warmer than room).
 * 
 * @param {number} uValue - Thermal transmittance [W/(m²·K)]
 * @param {number} area - Surface area [m²]
 * @param {number} tInside - Inside temperature [°C]
 * @param {number} tOutside - Outside temperature [°C]
 * @returns {number} Heat loss [W] (negative = heat gain)
 */
export const calculateTransmissionLoss = (uValue, area, tInside, tOutside) => {
  return uValue * area * (tInside - tOutside);
};

/**
 * Calculates heat loss through building envelope with 6-sided directional breakdown
 * 
 * Provides breakdown by surface orientation for visualization purposes.
 * Uses room dimensions from BUILDING_CONFIG for accurate area calculations.
 * 
 * Positive values = heat loss (room warmer than outside).
 * Negative values = heat gain (outside warmer than room).
 * 
 * @param {number} uValue - Overall U-value [W/(m²·K)]
 * @param {number} totalArea - Total envelope area [m²] (for reference/scaling)
 * @param {number} roomTemperature - Room temperature [°C]
 * @param {number} outsideTemperature - Outside temperature [°C]
 * @param {number} roofArea - Roof area [m²] (optional, uses config if 0)
 * @returns {Object} Detailed heat loss breakdown for all 6 sides
 */
export const calculateEnvelopeLoss = (uValue, totalArea, roomTemperature, outsideTemperature, roofArea = 0) => {
  const tempDiff = roomTemperature - outsideTemperature;
  
  // Get room dimensions from config
  const roomWidth = BUILDING_CONFIG.ROOM_WIDTH || 5;
  const roomDepth = BUILDING_CONFIG.ROOM_DEPTH || 4;
  const roomHeight = BUILDING_CONFIG.ROOM_HEIGHT || 2.8;
  
  // Calculate actual areas for each face
  const wallAreaNS = roomWidth * roomHeight;     // North/South (front/back)
  const wallAreaEW = roomDepth * roomHeight;     // East/West (sides)
  const floorCeilingArea = roofArea > 0 ? roofArea : roomWidth * roomDepth;
  
  // Calculate losses per surface (assuming all exterior)
  const lossNorth = uValue * wallAreaNS * tempDiff;
  const lossSouth = uValue * wallAreaNS * tempDiff;
  const lossEast = uValue * wallAreaEW * tempDiff;
  const lossWest = uValue * wallAreaEW * tempDiff;
  const lossAbove = uValue * 0.8 * floorCeilingArea * tempDiff; // Roof (better insulated)
  const lossBelow = uValue * 0.6 * floorCeilingArea * (roomTemperature - Math.max(outsideTemperature, 8)); // Ground
  
  const totalLoss = lossNorth + lossSouth + lossEast + lossWest + lossAbove + lossBelow;
  
  return {
    total: totalLoss,
    north: lossNorth,
    south: lossSouth,
    east: lossEast,
    west: lossWest,
    above: lossAbove,
    below: lossBelow,
  };
};

/**
 * Calculates heat transfer between adjacent rooms
 * 
 * Q = U × A × (T_room1 - T_room2)
 * 
 * @param {number} tRoom1 - Temperature of room 1 [°C]
 * @param {number} tRoom2 - Temperature of room 2 [°C]
 * @param {number} uValue - U-value between rooms [W/(m²·K)]
 * @param {number} area - Shared wall area [m²]
 * @returns {number} Heat flow from room 1 to room 2 [W] (positive = loss from room 1)
 */
export const calculateInterRoomHeatFlow = (tRoom1, tRoom2, uValue = BUILDING_CONFIG.DEFAULT_U_ROOM_TO_ROOM, area = BUILDING_CONFIG.WALL_AREA) => {
  return uValue * area * (tRoom1 - tRoom2);
};

/**
 * Calculates ventilation heat loss
 * Similar to calculateVentilationLoss in HeatPumpSimulation/utils.js
 * 
 * Q = ρ_air × V × c_p × n × (T_inside - T_outside)
 *   = 0.34 × V × n × (T_inside - T_outside)
 * 
 * where 0.34 Wh/(m³·K) is the volumetric heat capacity of air
 * 
 * Positive values represent heat loss (room warmer than outside).
 * Negative values represent heat gain (outside warmer than room).
 * 
 * @param {number} roomTemperature - Current room temperature [°C]
 * @param {number} outsideTemperature - Outside temperature [°C]
 * @param {number} roomVolume - Room volume [m³]
 * @param {number} airChangesPerHour - Air changes per hour [ACH] (e.g., 2 = air replaced twice per hour)
 * @returns {number} Ventilation heat loss [W] (negative = heat gain)
 */
export const calculateVentilationLoss = (roomTemperature, outsideTemperature, roomVolume, airChangesPerHour = 0.5) => {
  const temperatureDifference = roomTemperature - outsideTemperature;
  return AIR_PROPERTIES.VOLUMETRIC_HEAT_CAPACITY * roomVolume * airChangesPerHour * temperatureDifference;
};

/**
 * Calculates total room heat losses/gains for a 6-sided cube room model
 * 
 * Room geometry (cube with 6 faces):
 * - North wall: Front of building (always exterior)
 * - South wall: Back of building (always exterior)  
 * - East wall: May have neighbor room or exterior
 * - West wall: May have neighbor room or exterior
 * - Ceiling (above): May have room above or exterior (roof)
 * - Floor (below): May have room below or exterior (ground)
 * 
 * For each side:
 * - If neighbor exists: Q = U_internal × A × (T_room - T_neighbor)
 * - If exterior: Q = U_outdoor × A × (T_room - T_outside)
 * 
 * @param {Object} params - Room parameters
 * @param {number} params.tRoom - Room temperature [°C]
 * @param {number} params.tOutside - Outside temperature [°C]
 * @param {number} params.uOutdoor - U-value to outdoor [W/(m²·K)]
 * @param {number} params.outdoorArea - Total outdoor-facing area (used as reference) [m²]
 * @param {Object} params.neighbors - Adjacent room temps {north, south, east, west, above, below}
 * @param {number} params.airChangeRate - Air changes per hour [ACH]
 * @param {number} params.uInternal - U-value between rooms [W/(m²·K)] (optional)
 * @returns {Object} Complete heat loss breakdown by side and category
 */
export const calculateTotalRoomLosses = ({
  tRoom,
  tOutside,
  uOutdoor,
  outdoorArea,
  neighbors,
  airChangeRate = 0.5,
  uInternal = BUILDING_CONFIG.DEFAULT_U_ROOM_TO_ROOM,
}) => {
  // Room dimensions from building config
  const roomWidth = BUILDING_CONFIG.ROOM_WIDTH || 5;   // meters
  const roomDepth = BUILDING_CONFIG.ROOM_DEPTH || 4;   // meters  
  const roomHeight = BUILDING_CONFIG.ROOM_HEIGHT || 2.8; // meters
  
  // Calculate area of each face
  const wallAreaNS = roomWidth * roomHeight;  // North/South walls (front/back)
  const wallAreaEW = roomDepth * roomHeight;  // East/West walls (sides)
  const floorCeilingArea = roomWidth * roomDepth; // Floor and ceiling
  
  // Extract neighbor temperatures (null = exterior)
  const { north, south, east, west, above, below } = neighbors;
  
  // Initialize breakdown object for visualization
  const breakdown = {
    north: 0,
    south: 0,
    east: 0,
    west: 0,
    above: 0,  // ceiling/roof
    below: 0,  // floor/ground
  };
  
  // Track losses by category
  let transmissionLossToOutside = 0;
  let interRoomLoss = 0;
  
  // =========================================================================
  // NORTH WALL (front of building - always exterior)
  // =========================================================================
  breakdown.north = calculateTransmissionLoss(uOutdoor, wallAreaNS, tRoom, tOutside);
  transmissionLossToOutside += breakdown.north;
  
  // =========================================================================
  // SOUTH WALL (back of building - always exterior)
  // =========================================================================
  breakdown.south = calculateTransmissionLoss(uOutdoor, wallAreaNS, tRoom, tOutside);
  transmissionLossToOutside += breakdown.south;
  
  // =========================================================================
  // EAST WALL (side - may have neighbor or exterior)
  // =========================================================================
  if (east !== null) {
    // Has neighbor room to the east
    breakdown.east = calculateInterRoomHeatFlow(tRoom, east, uInternal, wallAreaEW);
    interRoomLoss += breakdown.east;
  } else {
    // Exterior wall
    breakdown.east = calculateTransmissionLoss(uOutdoor, wallAreaEW, tRoom, tOutside);
    transmissionLossToOutside += breakdown.east;
  }
  
  // =========================================================================
  // WEST WALL (side - may have neighbor or exterior)
  // =========================================================================
  if (west !== null) {
    // Has neighbor room to the west
    breakdown.west = calculateInterRoomHeatFlow(tRoom, west, uInternal, wallAreaEW);
    interRoomLoss += breakdown.west;
  } else {
    // Exterior wall
    breakdown.west = calculateTransmissionLoss(uOutdoor, wallAreaEW, tRoom, tOutside);
    transmissionLossToOutside += breakdown.west;
  }
  
  // =========================================================================
  // CEILING (above - may have room above or exterior/roof)
  // =========================================================================
  if (above !== null) {
    // Has room above
    breakdown.above = calculateInterRoomHeatFlow(tRoom, above, uInternal, floorCeilingArea);
    interRoomLoss += breakdown.above;
  } else {
    // Exterior roof - typically higher U-value for roofs
    const roofU = uOutdoor * 0.8; // Roofs often better insulated
    breakdown.above = calculateTransmissionLoss(roofU, floorCeilingArea, tRoom, tOutside);
    transmissionLossToOutside += breakdown.above;
  }
  
  // =========================================================================
  // FLOOR (below - may have room below or exterior/ground)
  // =========================================================================
  if (below !== null) {
    // Has room below
    breakdown.below = calculateInterRoomHeatFlow(tRoom, below, uInternal, floorCeilingArea);
    interRoomLoss += breakdown.below;
  } else {
    // Ground floor - ground temperature is more stable than outdoor
    // Use a moderated ground temperature (typically ~10°C or between tRoom and tOutside)
    const groundTemp = Math.max(tOutside, 8); // Ground doesn't get as cold as air
    const groundU = uOutdoor * 0.6; // Ground floors often have some insulation
    breakdown.below = calculateTransmissionLoss(groundU, floorCeilingArea, tRoom, groundTemp);
    transmissionLossToOutside += breakdown.below;
  }
  
  // =========================================================================
  // VENTILATION LOSS (unchanged - heat lost through air exchange)
  // =========================================================================
  const roomVolume = getDefaultRoomVolume();
  const ventilationLoss = calculateVentilationLoss(
    tRoom,
    tOutside,
    roomVolume,
    airChangeRate
  );
  
  // =========================================================================
  // TOTAL LOSS CALCULATION
  // =========================================================================
  const transmissionLoss = transmissionLossToOutside + interRoomLoss;
  const totalLoss = transmissionLoss + ventilationLoss;
  
  return {
    totalLoss,
    transmissionLoss,
    transmissionLossToOutside,
    ventilationLoss,
    interRoomLoss,
    breakdown,
    // Summary info for debugging/display
    summary: {
      exteriorSides: [north, south, east, west, above, below].filter(n => n === null).length,
      interiorSides: [north, south, east, west, above, below].filter(n => n !== null).length,
    },
  };
};

// #endregion HEAT LOSS CALCULATIONS

// #region THERMAL RESISTANCE (R-VALUE) CALCULATIONS
// ============================================================================
// THERMAL RESISTANCE (R-VALUE) CALCULATIONS
// ============================================================================

/**
 * Calculates thermal resistance (R-value) of room envelope
 * 
 * R = 1 / (U × A)
 * 
 * @param {number} uValue - U-value [W/(m²·K)]
 * @param {number} area - Surface area [m²]
 * @returns {number} Thermal resistance [K/W]
 */
export const calculateRValue = (uValue, area) => {
  if (uValue <= 0 || area <= 0) return Infinity;
  return 1 / (uValue * area);
};

// #endregion THERMAL RESISTANCE (R-VALUE) CALCULATIONS

// #region HEAT PUMP CALCULATIONS
// ============================================================================
// HEAT PUMP CALCULATIONS
// ============================================================================

/**
 * Calculates heat pump COP using Carnot efficiency with real-world correction
 * 
 * COP = η_c × T_hot / (T_hot - T_cold)
 * 
 * @param {number} tSupply - Supply water temperature [°C]
 * @param {number} tOutdoor - Outdoor temperature [°C]
 * @returns {number} Coefficient of Performance
 */
export const calculateHeatPumpCOP = (tSupply, tOutdoor) => {
  const { COMPRESSOR_EFFICIENCY, EVAPORATOR_APPROACH, CONDENSER_APPROACH } = HEAT_PUMP_CONFIG;
  
  // Condenser temperature (water supply + approach)
  const tCondenser = celsiusToKelvin(tSupply + CONDENSER_APPROACH);
  
  // Evaporator temperature (outdoor - approach)
  const tEvaporator = celsiusToKelvin(tOutdoor - EVAPORATOR_APPROACH);
  
  // Carnot COP with real efficiency
  const carnotCOP = tCondenser / (tCondenser - tEvaporator);
  const realCOP = COMPRESSOR_EFFICIENCY * carnotCOP;
  
  // Practical limits
  return Math.max(1.5, Math.min(realCOP, 8.0));
};

/**
 * Calculates mixed return water temperature from all circuits
 * 
 * T_mix = Σ(ṁ_i × T_i) / Σ(ṁ_i)
 * 
 * @param {Array<{massFlow: number, temperature: number}>} circuits - Array of circuit data
 * @returns {number} Mixed temperature [°C]
 */
export const calculateMixedReturnTemperature = (circuits) => {
  let totalMassFlowTemp = 0;
  let totalMassFlow = 0;
  
  circuits.forEach(({ massFlow, temperature }) => {
    totalMassFlowTemp += massFlow * temperature;
    totalMassFlow += massFlow;
  });
  
  if (totalMassFlow < 1e-9) return 0;
  
  return totalMassFlowTemp / totalMassFlow;
};

// #endregion HEAT PUMP CALCULATIONS

// #region THERMOSTAT SIMULATION
// ============================================================================
// THERMOSTAT SIMULATION
// ============================================================================

/**
 * Calculates thermostat valve position based on room temperature
 * Uses incremental proportional control (similar to HeatPump simulation)
 * 
 * @param {number} tRoom - Current room temperature [°C]
 * @param {number} tSetpoint - Desired temperature [°C]
 * @param {number} currentOpening - Current thermostat opening (0-1)
 * @returns {number} Valve opening (0-1)
 */
export const calculateThermostatPosition = (tRoom, tSetpoint, currentOpening) => {
  // Proportional control - adjust opening based on temperature error
  const error = tSetpoint - tRoom;
  const newOpening = currentOpening + 0.1 * error;
  
  return Math.max(0, Math.min(1, newOpening));
};

/**
 * Updates room temperature based on heat balance
 * Similar to updateRoomTemperature in HeatPumpSimulation/utils.js
 * 
 * dT/dt = (Q_in - Q_out) / C_thermal
 * 
 * @param {Object} params - Temperature update parameters
 * @param {number} params.currentTemperature - Current room temperature [°C]
 * @param {number} params.targetTemperature - Desired temperature [°C]
 * @param {number} params.heatInput - Heat input from radiator [W]
 * @param {number} params.heatLoss - Total heat loss [W]
 * @param {number} params.thermostatOpening - Current thermostat opening (0-1)
 * @param {number} params.roomHeatCapacity - Room thermal mass [J/K]
 * @returns {Object} { temperature, temperatureChange, thermostatOpening }
 */
export const updateRoomTemperature = ({
  currentTemperature,
  targetTemperature,
  heatInput,
  heatLoss,
  thermostatOpening,
  roomHeatCapacity = ROOM_DEFAULTS.THERMAL_CAPACITANCE,
}) => {
  // Calculate net heat and temperature change
  const netHeat = heatInput - heatLoss;
  const temperatureChange = netHeat / roomHeatCapacity;
  const newTemperature = currentTemperature + temperatureChange;
  
  // Proportional thermostat control adjustment
  const error = targetTemperature - newTemperature;
  const newThermostatOpening = clamp(thermostatOpening + 0.1 * error, 0, 1);
  
  return {
    temperature: newTemperature,
    temperatureChange,
    thermostatOpening: newThermostatOpening,
  };
};

// #endregion THERMOSTAT SIMULATION

// #region SIMULATION STEP CALCULATION
// ============================================================================
// SIMULATION STEP CALCULATION
// ============================================================================

/**
 * Performs one simulation step for a room
 * Updates temperature based on heat balance
 * 
 * Heat balance equation:
 * dT/dt = (Q_radiator - Q_losses) / C_thermal
 * 
 * Where Q_losses includes all heat transfer mechanisms and can be:
 * - Positive: room losing heat (room warmer than environment)
 * - Negative: room gaining heat (environment warmer than room)
 * 
 * @param {Object} params - Simulation parameters
 * @param {Object} params.room - Room state
 * @param {number} params.tOutdoor - Outdoor temperature [°C]
 * @param {number} params.tSupply - Supply water temperature [°C]
 * @param {number} params.kRad - Radiator coefficient [W/K^n]
 * @param {Object} params.neighbors - Neighbor temperatures
 * @param {number} params.systemPressure - System pressure [bar]
 * @param {number} params.floorIndex - Floor number
 * @param {number} params.columnIndex - Position on floor
 * @param {number} params.pipeLength - Pipe length [m]
 * @param {number} params.pipeDiameter - Pipe diameter [mm]
 * @param {number} params.kvMax - Max valve Kv
 * @param {number} params.airChangeRate - Air change rate [ACH]
 * @returns {Object} Updated room state
 */
export const simulateRoomStep = ({
  room,
  tOutdoor,
  tSupply,
  kRad,
  neighbors,
  systemPressure,
  floorIndex,
  columnIndex,
  pipeLength,
  pipeDiameter,
  kvMax,
  airChangeRate = 0.5,
}) => {
  const tRoom = room.Tc[room.Tc.length - 1];
  
  // 1. Calculate flow rate through this branch (using CURRENT thermostat opening)
  const volumeFlow = calculateBranchFlow({
    floorIndex,
    columnIndex,
    systemPressure,
    pipeLength,
    pipeDiameter,
    kvMax,
    presetOpening: room.valve,
    thermostatOpening: room.kThermostat,
  });
  
  const massFlow = volumeFlow * WATER_PROPERTIES.DENSITY;
  
  // 2. Calculate radiator performance
  const { Treturn, heatOutput } = calculateRadiatorPerformance({
    Tsupply: tSupply,
    Troom: tRoom,
    massFlow,
    nSections: room.nRad,
    kRad,
  });
  
  // 3. Calculate heat losses
  const { totalLoss } = calculateTotalRoomLosses({
    tRoom,
    tOutside: tOutdoor,
    uOutdoor: room.Uout,
    outdoorArea: BUILDING_CONFIG.WALL_AREA,
    neighbors,
    airChangeRate,
  });
  
  // 4. Calculate temperature change
  // dT/dt = (Q_in - Q_out) / C_thermal
  const netHeat = heatOutput - totalLoss;
  const dT = (netHeat * SIMULATION_CONFIG.SIMULATED_TIME_STEP) / ROOM_DEFAULTS.THERMAL_CAPACITANCE;
  
  // Update temperature
  const newTemp = tRoom + dT;
  
  // 5. Calculate NEW thermostat position based on RESULTING temperature
  const thermostatOpening = calculateThermostatPosition(newTemp, room.Treq, room.kThermostat);
  // Update history arrays
  const newTc = [...room.Tc, newTemp].slice(-SIMULATION_CONFIG.MAX_HISTORY_LENGTH);
  const newMFlow = [...room.mFlow, massFlow].slice(-SIMULATION_CONFIG.MAX_HISTORY_LENGTH);
  const newQrad = [...room.qrad, heatOutput].slice(-SIMULATION_CONFIG.MAX_HISTORY_LENGTH);
  
  return {
    ...room,
    Tc: newTc,
    mFlow: newMFlow,
    qrad: newQrad,
    kThermostat: thermostatOpening,
    Tout: Treturn,
  };
};

// #endregion SIMULATION STEP CALCULATION

// #region HELPER FUNCTIONS
// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Converts mass flow from kg/s to liters per minute
 * @param {number} massFlow - Mass flow [kg/s]
 * @returns {number} Volume flow [L/min]
 */
export const massFlowToLPM = (massFlow) => {
  return (massFlow / WATER_PROPERTIES.DENSITY) * 1000 * 60;
};

/**
 * Converts liters per minute to mass flow in kg/s
 * @param {number} lpm - Volume flow [L/min]
 * @returns {number} Mass flow [kg/s]
 */
export const lpmToMassFlow = (lpm) => {
  return (lpm / 60 / 1000) * WATER_PROPERTIES.DENSITY;
};

/**
 * Gets neighbor temperatures for a room in the building grid (6 sides of a cube)
 * 
 * Coordinate system:
 * - north/south: rooms on the same floor (horizontal, along columns)
 * - east/west: rooms on the same floor (horizontal, along rows)  
 * - above/below: rooms on different floors (vertical)
 * 
 * @param {Array<Array<Object>>} rooms - Building grid [floor][column]
 * @param {number} i - Floor index (0 = ground floor)
 * @param {number} j - Column index
 * @returns {Object} { north, south, east, west, above, below } - neighbor temps or null if exterior
 */
export const getNeighborTemperatures = (rooms, i, j) => {
  const getTemp = (room) => room ? room.Tc[room.Tc.length - 1] : null;
  
  return {
    // Horizontal neighbors on same floor
    east: rooms[i]?.[j + 1] ? getTemp(rooms[i][j + 1]) : null,
    west: j > 0 && rooms[i]?.[j - 1] ? getTemp(rooms[i][j - 1]) : null,
    // For north/south we use the building's front/back walls
    // In this 2D grid, north and south are always exterior (no neighbors)
    north: null, // Always exterior (front of building)
    south: null, // Always exterior (back of building)
    // Vertical neighbors (floors above/below)
    above: rooms[i + 1]?.[j] ? getTemp(rooms[i + 1][j]) : null,
    below: i > 0 && rooms[i - 1]?.[j] ? getTemp(rooms[i - 1][j]) : null,
  };
};

// #endregion HELPER FUNCTIONS

/**
 * Checks if a room can be placed at given position (structural stability)
 * Rules:
 * 1. Can always place on ground floor (i === 0)
 * 2. Can place if there's a room directly below at (i-1, j)
 * 3. Can place if adjacent room is part of a connected horizontal group 
 *    with at least one room that has support below
 * 
 * @param {Array<Array<Object>>} rooms - Current building grid
 * @param {number} i - Floor index
 * @param {number} j - Column index
 * @returns {boolean} True if placement is valid
 */
export const canPlaceRoom = (rooms, i, j) => {
  // Already occupied
  if (rooms[i]?.[j]) return false;
  
  // Ground floor is always valid
  if (i === 0) return true;
  
  // Check if there's direct support below
  if (rooms[i - 1]?.[j]) return true;
  
  // Find all connected rooms on the same floor starting from adjacent positions
  const visited = new Set();
  const connectedRooms = [];
  
  const dfs = (col) => {
    if (col < 0 || col >= BUILDING_CONFIG.MAX_ROOMS_PER_FLOOR) return;
    if (visited.has(col)) return;
    if (!rooms[i]?.[col]) return;
    
    visited.add(col);
    connectedRooms.push(col);
    dfs(col - 1);  // Check left
    dfs(col + 1);  // Check right
  };
  
  // Start DFS from adjacent rooms (left and right)
  if (j > 0) dfs(j - 1);
  if (j < BUILDING_CONFIG.MAX_ROOMS_PER_FLOOR - 1) dfs(j + 1);
  
  // If connected rooms exist, check if any has support below
  for (const col of connectedRooms) {
    if (rooms[i - 1]?.[col]) {
      return true;
    }
  }
  
  return false;
};

/**
 * Checks if a room above would remain structurally stable after room deletion
 * 
 * Stability rules by column position:
 * - Left column (j=0): Needs room to the right with support, or right-right with support
 * - Middle column (j=1): Needs either left with support OR right with support
 * - Right column (j=2): Needs room to the left with support, or left-left with support
 * 
 * @param {number} j - Column index of room being checked above
 * @param {Array<Object>} floor - Floor where room exists (to be checked)
 * @param {Array<Object>} floorUnder - Floor below (to check support)
 * @returns {boolean} True if room would remain stable
 */
const checkRoomAboveIsStable = (j, floor, floorUnder) => {
  // Handle cases where floor or floorUnder don't exist
  if (!floor || !floorUnder) return true;
  
  switch (j) {
    case 0: {
      // Left column: stable if right neighbor exists with support below
      // OR right-right neighbor exists with support below right neighbor
      return (floor[1] && (floorUnder[1] || (floor[2] && floorUnder[2])));
    }
    
    case 1: {
      // Middle column: stable if left neighbor has support OR right neighbor has support
      return (floor[0] && floorUnder[0]) || (floor[2] && floorUnder[2]);
    }
    
    case 2: {
      // Right column: stable if left neighbor exists with support below
      // OR left-left neighbor exists with support below left neighbor
      return (floor[1] && (floorUnder[1] || (floor[0] && floorUnder[0])));
    }
    
    default:
      // For positions beyond typical 3-room layouts, use simplified logic
      return j === 0 || j === BUILDING_CONFIG.MAX_ROOMS_PER_FLOOR - 1;
  }
};

/**
 * Checks if neighboring rooms would remain structurally stable after room deletion
 * 
 * Stability rules by column position:
 * - Left column (j=0): Always stable (no left neighbor to affect)
 * - Middle column (j=1): Stable if left neighbor has support below AND right neighbor has support below
 * - Right column (j=2): Always stable (no right neighbor to affect)
 * 
 * @param {number} j - Column index of room being deleted
 * @param {Array<Object>} floor - Current floor (with room to be deleted)
 * @param {Array<Object>} floorUnder - Floor below (to check support)
 * @returns {boolean} True if all neighbors would remain stable
 */
const checkNeighboursAreStable = (j, floor, floorUnder) => {
  // Handle cases where floor or floorUnder don't exist
  if (!floor || !floorUnder) return true;
  
  switch (j) {
    case 0: {
      // Left column: no left neighbor, always stable
      return true;
    }
    
    case 1: {
      // Middle column: check both left and right neighbors
      // Left neighbor is stable if it doesn't exist or has support below
      const leftStable = !floor[0] || floorUnder[0];
      // Right neighbor is stable if it doesn't exist or has support below
      const rightStable = !floor[2] || floorUnder[2];
      return leftStable && rightStable;
    }
    
    case 2: {
      // Right column: no right neighbor, always stable
      return true;
    }
    
    default:
      // For positions beyond typical 3-room layouts
      return j === 0 || j === BUILDING_CONFIG.MAX_ROOMS_PER_FLOOR - 1;
  }
};

/**
 * Determines if a room can be safely deleted while maintaining structural integrity
 * 
 * A room can be deleted if:
 * 1. At least one other room remains in the building
 * 2. Any room directly above would still be supported
 * 3. All neighboring rooms would still have adequate support
 * 
 * The deletion algorithm checks:
 * - Ground floor rooms: Neighbors are always checked for stability
 * - Upper floor rooms: Room above must remain stable (if it exists)
 * - All rooms: Neighbors must remain stable after deletion
 * 
 * @param {Array<Array<Object>>} rooms - Building grid [floor][column]
 * @param {number} i - Floor index
 * @param {number} j - Column index
 * @returns {boolean} True if deletion is structurally safe
 */
export const canDeleteRoom = (rooms, i, j) => {
  // Requirement 1: Must have at least one room remaining in the building
  const totalRooms = rooms.flat().filter(Boolean).length;
  if (totalRooms <= 1) return false;
  
  // Get references to current floor and adjacent floors
  const floor = rooms[i] || [];
  const floorAbove = rooms[i + 1] || null;
  const floorBelow = i > 0 ? rooms[i - 1] : null;
  
  // Requirement 2: Check stability of room above (if it exists)
  let isDeletable = true;
  
  if (floorAbove && floorAbove[j]) {
    // Room above exists - it must remain stable after deletion
    const roomAboveStable = checkRoomAboveIsStable(j, floorAbove, floor);
    isDeletable = isDeletable && roomAboveStable;
  }
  
  // Requirement 3: Check stability of neighboring rooms
  // On ground floor, neighbors are always stable; on upper floors, check support below
  const neighboursStable = (i === 0) || checkNeighboursAreStable(j, floor, floorBelow);
  isDeletable = isDeletable && neighboursStable;
  
  return isDeletable;
};

// #endregion HELPER FUNCTIONS
